import React, { createContext, useEffect, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from 'react-apollo';

// MUTATIONS
import CREATE_CAMPAIGN_ORDER_REQUESTOR from '../../GraphQl/Mutations/CREATE_CAMPAIGN_ORDER_REQUESTOR';
import UPDATE_CAMPAIGN_ORDER_REQUESTOR from '../../GraphQl/Mutations/UPDATE_CAMPAIGN_ORDER_REQUESTOR';

// QUERIES
import CURRENT_USER from '../../GraphQl/Queries/CURRENT_USER';
import GET_CAMPAIGN_ORDER_REQUESTOR_ADVERTISERS from '../../GraphQl/Queries/GET_CAMPAIGN_ORDER_REQUESTOR_ADVERTISERS';
import GET_CAMPAIGN_ORDER_REQUESTOR from '../../GraphQl/Queries/GET_CAMPAIGN_ORDER_REQUESTOR';
import GET_CAMPAIGN_ORDERS_REQUESTOR from '../../GraphQl/Queries/GET_CAMPAIGN_ORDERS_REQUESTOR';
import GET_CAMPAIGN_ORDER_REQUESTOR_ADVERTISER from '../../GraphQl/Queries/GET_CAMPAIGN_ORDER_REQUESTOR_ADVERTISER';
import GET_BASIC_TAG_ACTIVITY from '../../GraphQl/Queries/GET_BASIC_TAG_ACTIVITY';

import { CampaignConfig } from '../../../platform/ux/Campaigns/shared/configs/campaignConfig';

const CampaignRequestorContext = createContext({});

export const CampaignRequestorProvider = (props) => {
  const { children } = props;
  const [selectedAdvertiser, setSelectedAdvertiser] = useState(null);
  const [currentUserOrg, setCurrentUserOrg] = useState(null);
  const [campaignOrdersConnection, setCampaignOrdersConnection] = useState([]);
  const [kpis, setKpis] = useState(CampaignConfig.kpiDefaultOptions);
  const [tagActivityData, setTagActivityData] = useState(null);
  const [showActivityPopconfirm, setShowActivityPopconfirm] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const previewUserData = (() => {
    try {
      const userData = localStorage.getItem('userData');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error parsing userData from localStorage:', error);
      return null;
    }
  })();

  // QUERIES
  const [fetchChildrenOrgs, { data: dataChildrenOrgs, loading: loadingChildrenOrgs }] =
    useLazyQuery(GET_CAMPAIGN_ORDER_REQUESTOR_ADVERTISERS, {
      onCompleted: (data) => {
        console.log('GET_CAMPAIGN_ORDER_REQUESTOR_ADVERTISERS completed:', data);
      },
    });

  const [fetchSelectedAdvertiser] = useLazyQuery(GET_CAMPAIGN_ORDER_REQUESTOR_ADVERTISER, {
    onCompleted: ({ org }) => {
      console.log('GET_CAMPAIGN_ORDER_REQUESTOR_ADVERTISER completed:', { org });
      setSelectedAdvertiser(org);
    },
  });

  const [fetchCampaignOrder, { data: dataCampaignOrder, loading: loadingCampaignOrder }] =
    useLazyQuery(GET_CAMPAIGN_ORDER_REQUESTOR, {
      fetchPolicy: 'cache-and-network',
    });

  const [
    fetchCampaignOrdersConnection,
    { data: dataCampaignOrdersConnection, loading: loadingCampaignOrdersConnection },
  ] = useLazyQuery(GET_CAMPAIGN_ORDERS_REQUESTOR, {
    fetchPolicy: 'cache-and-network',
  });

  const [getBasicTagActivityDirect, { loading: loadingBasicTagActivity }] = useLazyQuery(GET_BASIC_TAG_ACTIVITY, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      console.log('Basic tag activity data:', data.getBasicTagActivity);
      if (data.getBasicTagActivity && data.getBasicTagActivity.retailID[0].transactions === 0) {
        setTagActivityData(data.getBasicTagActivity);
        setShowActivityPopconfirm(true);
      }
    },
  });

  const {
    data,
    loading: loadingCurrentUser,
    error: currentUserError,
  } = useQuery(CURRENT_USER, {
    onCompleted: ({ currentUser }) => {
      console.log('CURRENT_USER completed:', { currentUser });
      if (currentUser && currentUser.defaultRole) {
        const currentOrg =
          (previewUserData && previewUserData.role && previewUserData.role.org) ||
          currentUser.defaultRole.org;
        console.log('Current org from CURRENT_USER:', currentOrg);
        setSelectedAdvertiser(currentOrg);
      }
    },
    onError: (error) => {
      console.error('Error fetching current user:', error);
    },
  });

  const currentUser = data?.currentUser;

  // MUTATIONS
  const [createCampaignOrder, { loading: loadingCreateCampaignOrder }] = useMutation(
    CREATE_CAMPAIGN_ORDER_REQUESTOR,
    {
      onCompleted: ({ createCampaignOrderRequestor }) => {
        fetchCampaignOrder({
          variables: {
            where: {
              id: createCampaignOrderRequestor.id,
            },
          },
        });
      },
    }
  );

  const [updateCampaignOrder, { loading: loadingUpdateCampaignOrder }] = useMutation(
    UPDATE_CAMPAIGN_ORDER_REQUESTOR,
    {
      update: (cache, { data: { updateCampaignOrderRequestor } }) => {
        cache.writeQuery({
          query: GET_CAMPAIGN_ORDER_REQUESTOR,
          variables: {
            where: {
              id: updateCampaignOrderRequestor.id,
            },
          },
          data: {
            campaignOrder: updateCampaignOrderRequestor,
          },
        });
      },
    }
  );

  // TODO: This should be movd to user context
  useEffect(() => {
    if (currentUser) {
      const currentOrg =
        (previewUserData && previewUserData.role && previewUserData.role.org) ||
        currentUser.defaultRole.org;
      console.log('Setting currentUserOrg in useEffect:', currentOrg);
      setCurrentUserOrg(currentOrg);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    if (dataCampaignOrdersConnection && dataCampaignOrdersConnection.campaignOrdersConnection) {
      setCampaignOrdersConnection(
        dataCampaignOrdersConnection.campaignOrdersConnection.edges.map((edge) => edge.node)
      );
      setPagination({
        ...pagination,
        total: dataCampaignOrdersConnection.full.aggregate.count,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataCampaignOrdersConnection]);

  return (
    <CampaignRequestorContext.Provider
      value={{
        currentUser,
        loadingCurrentUser,
        currentUserError,
        childrenOrg: dataChildrenOrgs ? dataChildrenOrgs.orgs : [],
        loadingChildrenOrgs,
        selectedAdvertiser,
        currentUserOrg,
        currentCampaignOrder: dataCampaignOrder ? dataCampaignOrder.campaignOrder : null,
        loadingCreateCampaignOrder,
        loadingCampaignOrder,
        loadingUpdateCampaignOrder,
        campaignOrdersConnection,
        loadingCampaignOrdersConnection,
        pagination,
        kpis,
        tagActivityData,
        showActivityPopconfirm,
        loadingBasicTagActivity,
        setShowActivityPopconfirm,
        setKpis,
        createCampaignOrder,
        updateCampaignOrder,
        setSelectedAdvertiser,
        fetchCampaignOrder,
        fetchCampaignOrdersConnection,
        setPagination,
        fetchChildrenOrgs,
        fetchSelectedAdvertiser,
        getBasicTagActivityDirect,
      }}
      {...props}
    >
      {loadingCurrentUser ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          Loading...
        </div>
      ) : (
        children
      )}
    </CampaignRequestorContext.Provider>
  );
};

export default CampaignRequestorContext;
